import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { notify } from '../utils/notify';

// Cartão Pix Cards (Brendi): fatura do Stark Bank → títulos de contas a pagar no Omie.
// Fluxo: importar fatura → escolher mês e data de pagamento → categoria sugerida pelo histórico
// (ou escolhida no select) → lançar uma ou várias linhas, sempre um título por chamada.
// Spec: docs/superpowers/specs/2026-10-28-cartao-pixcards-design.md

const URL = `${process.env.REACT_APP_API_BASE}/api/bpofin/cartao/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v));
const dataBr = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '-');
const msgErro = (e) => e?.response?.data?.mensagem || e?.message || 'Falha inesperada';
const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Últimos 18 meses, do mais recente ao mais antigo, para o select de mês da fatura.
const opcoesMes = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 18; i += 1) {
    out.push({ ano: d.getFullYear(), mes: d.getMonth() + 1 });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

function Modal({ titulo, perigo, children, rodape, onFechar, ocupado }) {
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1080 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
              <i className={`fas ${perigo ? 'fa-triangle-exclamation text-danger' : 'fa-credit-card'} me-2`} style={perigo ? undefined : { color: '#00b5cc' }}></i>{titulo}
            </h5>
            <button type="button" className="btn-close" onClick={onFechar} disabled={ocupado}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer bg-light">{rodape}</div>
        </div>
      </div>
    </div>
  );
}

export default function BpoFinCartaoPixCards() {
  const arquivoRef = useRef(null);
  const [fatura, setFatura] = useState(null);      // {linhas, periodo, sugestao, nome}
  const [ano, setAno] = useState('');
  const [mes, setMes] = useState('');
  const [prep, setPrep] = useState(null);          // {linhas (com sugestao/status), categorias {D,R}, data_pagamento, historico}
  const [dataPg, setDataPg] = useState('');
  const [conciliar, setConciliar] = useState(true);
  const [catLinha, setCatLinha] = useState({});    // idx → categoria escolhida no select
  const [feitos, setFeitos] = useState({});        // idx → {tipo, codigo}
  const [sel, setSel] = useState([]);              // idx marcados para o lote
  const [filtro, setFiltro] = useState('pendentes'); // 'pendentes' | 'omie' | 'todas'
  const [ocupado, setOcupado] = useState('');      // 'fatura' | 'preparar' | 'acao'
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState(null);        // {tipo: 'lancar', linha} | {tipo: 'lote'}
  const [lote, setLote] = useState(null);          // {total, feitos, atual, erros: [{idx, erro}]}
  const [forn, setForn] = useState({});            // estabelecimento → {situacao: 'igual'|'parecido'|'sem', codigo, nome_omie, candidatos, criado}
  const [criando, setCriando] = useState('');      // estabelecimento cujo fornecedor está sendo criado

  const importar = async (e) => {
    const arq = e.target.files?.[0];
    if (!arq) return;
    setOcupado('fatura'); setErro(''); setPrep(null); setFeitos({}); setSel([]); setCatLinha({});
    try {
      const fd = new FormData();
      fd.append('arquivo', arq);
      const { data } = await axios.post(URL, fd, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setFatura(data);
      setAno(String(data.sugestao.ano)); setMes(String(data.sugestao.mes));
    } catch (err) { setErro(msgErro(err)); setFatura(null); } finally { setOcupado(''); if (arquivoRef.current) arquivoRef.current.value = ''; }
  };

  // Mês escolhido → histórico, status no Omie e data de pagamento sugerida.
  const preparar = async () => {
    if (!fatura || !ano || !mes) return;
    setOcupado('preparar'); setErro(''); setPrep(null); setSel([]); setFeitos({}); setCatLinha({});
    try {
      const { data } = await axios.post(URL, { acao: 'preparar', ano: Number(ano), mes: Number(mes), linhas: fatura.linhas },
        { headers: headers(), timeout: 300000 });
      setPrep(data);
      setForn(data.fornecedores || {});
      setDataPg(data.data_pagamento || '');
    } catch (err) { setErro(msgErro(err)); } finally { setOcupado(''); }
  };
  useEffect(() => { preparar(); }, [fatura, ano, mes]);   // eslint-disable-line react-hooks/exhaustive-deps

  const linhas = prep?.linhas || [];
  // Sugestão só vale se for do tipo da linha (receita para crédito, despesa para compra): no Omie a
  // categoria decide o sinal do lançamento em conta.
  const categoriaDe = (l) => {
    if (catLinha[l.idx] !== undefined) return catLinha[l.idx];
    const sug = l.sugestao?.codigo || '';
    const lista = prep?.categorias?.[l.valor >= 0 ? 'R' : 'D'] || [];
    return sug && lista.some((c) => c.codigo === sug) ? sug : '';
  };
  const fornDe = (l) => forn[l.estabelecimento] || null;
  const fornCodigo = (l) => (l.valor >= 0 ? null : fornDe(l)?.codigo || null);
  const lancavel = (l) => !feitos[l.idx] && !l.ignorar && l.status !== 'omie' && !!categoriaDe(l) && (l.valor >= 0 || !!fornCodigo(l));

  // Fornecedor: usar um cadastro parecido já existente, ou criar um novo (uma chamada por fornecedor).
  const usarCandidato = (nome, cand) => setForn((m) => ({ ...m, [nome]: { situacao: 'igual', codigo: cand.codigo, nome_omie: cand.nome, candidatos: [], escolhido: true } }));
  const criarFornecedor = async (nome) => {
    setCriando(nome); setErro('');
    try {
      const { data } = await axios.post(URL, { acao: 'fornecedor_criar', nome }, { headers: headers(), timeout: 120000 });
      setForn((m) => ({ ...m, [nome]: { situacao: 'igual', codigo: data.codigo, nome_omie: data.nome, candidatos: [], criado: data.criado } }));
      notify.sucesso(data.criado ? `Fornecedor "${nome}" criado no Omie · cód. ${data.codigo}` : `"${nome}" já existia no Omie · cód. ${data.codigo}`);
    } catch (err) { const m = msgErro(err); setErro(m); notify.erro(m); } finally { setCriando(''); }
  };
  const pendente = (l) => !feitos[l.idx] && !l.ignorar && l.status !== 'omie';
  // Em Pendentes, a linha lançada nesta sessão fica visível (verde, selo Lançado) em vez de sumir na hora.
  const filtradas = linhas.filter((l) => (filtro === 'todas' ? true : filtro === 'omie' ? l.status === 'omie' || !!feitos[l.idx] : pendente(l) || !!feitos[l.idx]));
  const lancaveis = filtradas.filter(lancavel).map((l) => l.idx);
  const todosMarcados = lancaveis.length > 0 && lancaveis.every((i) => sel.includes(i));
  const totais = useMemo(() => {
    const compras = linhas.filter((l) => l.valor < 0 && !l.ignorar);
    const creditos = linhas.filter((l) => l.valor > 0 && !l.ignorar);
    return {
      n: linhas.length, compras: compras.reduce((s, l) => s + l.valor, 0), creditos: creditos.reduce((s, l) => s + l.valor, 0),
      omie: linhas.filter((l) => l.status === 'omie').length, pendentes: linhas.filter(pendente).length,
      semCategoria: linhas.filter((l) => pendente(l) && !categoriaDe(l)).length,
      semFornecedor: [...new Set(linhas.filter((l) => pendente(l) && l.valor < 0 && !fornCodigo(l)).map((l) => l.estabelecimento))].length,
    };
  }, [linhas, feitos, catLinha, forn]);   // eslint-disable-line react-hooks/exhaustive-deps

  const dataPgValida = /^\d{2}\/\d{2}\/\d{4}$/.test(dataPg);

  const corpoLancar = (l) => ({ acao: 'lancar', ano: Number(ano), mes: Number(mes), data_pagamento: dataPg, conciliar, linha: l, categoria: categoriaDe(l), fornecedor: fornCodigo(l) });

  const lancarUma = async (l) => {
    setOcupado('acao'); setErro('');
    try {
      const { data } = await axios.post(URL, corpoLancar(l), { headers: headers(), timeout: 120000 });
      setFeitos((f) => ({ ...f, [l.idx]: { tipo: data.tipo, codigo: data.codigo } }));
      setSel((s) => s.filter((i) => i !== l.idx));
      setModal(null);
      notify.sucesso(`${data.tipo === 'titulo' ? 'Título' : 'Crédito'} lançado no Omie · cód. ${data.codigo}`);
    } catch (err) { const m = msgErro(err); setErro(m); notify.erro(m); } finally { setOcupado(''); }
  };

  const lancarLote = async () => {
    const itens = linhas.filter((l) => sel.includes(l.idx) && lancavel(l));
    setOcupado('acao'); setErro('');
    const prog = { total: itens.length, feitos: 0, atual: null, erros: [] };
    setLote({ ...prog });
    for (const l of itens) {
      prog.atual = l; setLote({ ...prog });
      try {
        const { data } = await axios.post(URL, corpoLancar(l), { headers: headers(), timeout: 120000 });
        setFeitos((f) => ({ ...f, [l.idx]: { tipo: data.tipo, codigo: data.codigo } }));
        prog.feitos += 1;
      } catch (err) {
        prog.erros.push({ idx: l.idx, estabelecimento: l.estabelecimento, valor: l.valor, erro: msgErro(err) });
        break;   // para no primeiro erro: o operador decide
      }
      setLote({ ...prog });
    }
    prog.atual = null; setLote({ ...prog });
    setSel((s) => s.filter((i) => !itens.some((l) => l.idx === i && !prog.erros.some((e) => e.idx === i))));
    setOcupado('');
    const msg = `${prog.feitos} de ${prog.total} lançado(s)${prog.erros.length ? ` · parou em: ${prog.erros[0].erro}` : ''}`;
    if (prog.erros.length) notify.erro(msg); else notify.sucesso(msg);
  };

  const Badge = ({ l }) => {
    const f = feitos[l.idx];
    if (f) return <span className="badge bg-success text-white" title={f.tipo === 'titulo' ? 'Título de contas a pagar criado e baixado' : 'Crédito em conta lançado'}><i className="fas fa-check me-1"></i>Lançado · cód. {f.codigo}</span>;
    if (l.ignorar) return <span className="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle" title="Já entra no Omie como transferência Pix Revenue → Pix Cards">Pagamento da fatura · não lança</span>;
    if (l.status === 'omie') return <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle" title={`Omie: ${l.fornecedor}`}>No Omie · cód. {l.nCodLancamento}</span>;
    return (
      <>
        {l.status === 'valor_igual' && <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle me-1" title={`Há no Omie um lançamento de ${brl(l.valor)} para "${l.fornecedor}" neste mês. Confira antes de lançar.`}>valor igual no Omie</span>}
        <button className="btn btn-sm btn-outline-primary" disabled={ocupado === 'acao' || !lancavel(l) || !dataPgValida} onClick={() => setModal({ tipo: 'lancar', linha: l })}
          title={!categoriaDe(l) ? 'Escolha a categoria' : (l.valor < 0 && !fornCodigo(l)) ? 'Escolha ou crie o fornecedor' : !dataPgValida ? 'Informe a data de pagamento' : ''}>
          <i className="fas fa-plus me-1"></i>Lançar
        </button>
      </>
    );
  };

  const Fornecedor = ({ l }) => {
    if (l.valor >= 0) return <span className="text-muted">crédito em conta, sem fornecedor</span>;
    const f = fornDe(l);
    const ocupadoAqui = criando === l.estabelecimento;
    if (!f) return <span className="text-muted">-</span>;
    if (f.situacao === 'igual') {
      return (
        <span className={`badge border ${f.criado ? 'bg-info-subtle text-info-emphasis border-info-subtle' : 'bg-success-subtle text-success-emphasis border-success-subtle'}`}
          title={`Cadastro ${f.codigo}${f.escolhido ? ' (escolhido por você entre os parecidos)' : ''}`}>
          <i className={`fas ${f.criado ? 'fa-plus' : 'fa-check'} me-1`}></i>{f.nome_omie} · cód. {f.codigo}{f.criado ? ' · criado agora' : ''}
        </span>
      );
    }
    const podeEditar = pendente(l);
    return (
      <div className="d-flex flex-column gap-1">
        {f.situacao === 'parecido' && f.candidatos.map((c) => (
          <div key={c.codigo} className="d-flex align-items-center gap-1">
            <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" title={`Similaridade ${Math.round(c.similaridade * 100)}%`}>parecido: {c.nome} · cód. {c.codigo}</span>
            {podeEditar && <button type="button" className="btn btn-sm btn-outline-success py-0" onClick={() => usarCandidato(l.estabelecimento, c)} disabled={ocupado === 'acao'} title="Usar este cadastro para todas as compras deste estabelecimento">Usar</button>}
          </div>
        ))}
        {f.situacao === 'sem' && <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle">sem cadastro no Omie</span>}
        {podeEditar && (
          <button type="button" className="btn btn-sm btn-outline-primary py-0 align-self-start" onClick={() => criarFornecedor(l.estabelecimento)} disabled={!!criando || ocupado === 'acao'}
            title={`Criar o fornecedor "${l.estabelecimento}" no Omie (tag Fornecedor)`}>
            <i className={`fas ${ocupadoAqui ? 'fa-spinner fa-spin' : 'fa-plus'} me-1`}></i>Criar fornecedor
          </button>
        )}
      </div>
    );
  };

  const descCategoria = (l) => {
    const lista = prep?.categorias?.[l.valor >= 0 ? 'R' : 'D'] || [];
    const cod = categoriaDe(l);
    return lista.find((c) => c.codigo === cod)?.descricao || cod || '-';
  };

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-credit-card header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO Financeiro — Cartão Pix Cards</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 170 }} value={ano && mes ? `${ano}-${mes}` : ''} disabled={!fatura || !!ocupado}
            onChange={(e) => { const [a, m] = e.target.value.split('-'); setAno(a); setMes(m); }} aria-label="Mês da fatura">
            <option value="">{fatura ? 'Mês da fatura…' : 'Importe a fatura'}</option>
            {opcoesMes().map((o) => <option key={`${o.ano}-${o.mes}`} value={`${o.ano}-${o.mes}`}>{MESES[o.mes]}/{o.ano}</option>)}
          </select>
          <button type="button" className="gr-btn" onClick={() => arquivoRef.current?.click()} disabled={!!ocupado}>
            <i className={`fas ${ocupado === 'fatura' ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'} me-2`}></i>Importar fatura
          </button>
          <input ref={arquivoRef} type="file" accept=".xlsx,.csv" className="d-none" onChange={importar} />
          <button type="button" className="gr-btn" onClick={preparar} disabled={!!ocupado || !fatura || !ano || !mes} title="Reler o histórico e o extrato do Omie">
            <i className={`fas ${ocupado === 'preparar' ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger py-2">{erro}</div>}

      {!fatura && !ocupado && (
        <div className="alert alert-light border small">
          Importe a fatura do Stark Bank em csv ou xlsx (colunas <code>amount</code>, <code>holderName</code>, <code>created</code>, <code>description</code>).
          Cada compra vira um título de contas a pagar já pago na conta Pix Cards, com a categoria usada no passado para o mesmo estabelecimento.
        </div>
      )}

      {fatura && (
        <div className="d-flex flex-wrap gap-3 small mb-3 align-items-center">
          <span><b>{fatura.nome}</b> · {fatura.linhas.length} linhas · período {dataBr(fatura.periodo.de)} a {dataBr(fatura.periodo.ate)}</span>
          {prep && <>
            <span>Compras: <b className="text-danger">{brl(totais.compras)}</b></span>
            <span>Créditos: <b className="text-success">{brl(totais.creditos)}</b></span>
            <span>Já no Omie: <b>{totais.omie}</b></span>
            <span>Pendentes: <b className={totais.pendentes ? 'text-warning' : ''}>{totais.pendentes}</b>{totais.semCategoria > 0 && <span className="text-muted"> ({totais.semCategoria} sem categoria)</span>}</span>
            <span>Fornecedores a resolver: <b className={totais.semFornecedor ? 'text-danger' : ''}>{totais.semFornecedor}</b></span>
            <span className="text-muted">histórico: {prep.historico.estabelecimentos} estabelecimentos em {prep.historico.meses} meses · {prep.cadastros} cadastros no Omie</span>
          </>}
        </div>
      )}

      {fatura && fatura.descartadas?.length > 0 && (
        <div className="alert alert-warning py-2 small mb-3">
          <div className="fw-bold mb-1"><i className="fas fa-triangle-exclamation me-2"></i>{fatura.descartadas.length} linha(s) do arquivo não foram lidas. Confira e, se precisar, importe o CSV original do Stark.</div>
          <ul className="mb-0 ps-3">
            {fatura.descartadas.map((x) => <li key={x.idx}>Linha {x.idx}: {x.motivo === 'data' ? `data ilegível "${x.created}"` : `valor ilegível "${x.amount}"`}{x.estabelecimento ? ` · ${x.estabelecimento}` : ''}</li>)}
          </ul>
        </div>
      )}

      {ocupado === 'preparar' && <div className="text-muted small mb-3"><span className="spinner-border spinner-border-sm me-2"></span>Lendo o histórico da Pix Cards, os cadastros de fornecedores do Omie (a primeira vez leva cerca de um minuto) e conferindo o que já está lançado…</div>}

      {prep && (<>
        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          <label className="small d-flex align-items-center gap-2 m-0">Data de pagamento da fatura
            <input type="text" className={`form-control form-control-sm ${dataPgValida ? '' : 'is-invalid'}`} style={{ width: 120 }} value={dataPg} placeholder="dd/mm/aaaa"
              onChange={(e) => setDataPg(e.target.value)} disabled={ocupado === 'acao'} title="Vira emissão, vencimento e baixa dos títulos. Sugerida pela transferência de entrada na Pix Cards no mês." />
          </label>
          <label className="small d-flex align-items-center gap-2 m-0">
            <input type="checkbox" className="form-check-input m-0" checked={conciliar} onChange={(e) => setConciliar(e.target.checked)} disabled={ocupado === 'acao'} />Marcar como conciliado
          </label>
          <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="Filtro">
            <button type="button" className={`btn ${filtro === 'pendentes' ? 'btn-warning' : 'btn-outline-secondary'}`} onClick={() => setFiltro('pendentes')}>Pendentes ({totais.pendentes})</button>
            <button type="button" className={`btn ${filtro === 'omie' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setFiltro('omie')}>Já no Omie ({totais.omie + Object.keys(feitos).length})</button>
            <button type="button" className={`btn ${filtro === 'todas' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setFiltro('todas')}>Todas ({totais.n})</button>
          </div>
          {sel.length > 0 && (
            <button type="button" className="btn btn-sm btn-primary" disabled={ocupado === 'acao' || !dataPgValida} onClick={() => { setLote(null); setModal({ tipo: 'lote' }); }}>
              <i className="fas fa-plus me-1"></i>Lançar selecionados ({sel.length})
            </button>
          )}
        </div>

        <table className="table table-sm table-hover small align-middle">
          <thead><tr>
            <th style={{ width: 28 }}>
              <input type="checkbox" className="form-check-input" aria-label="Marcar todos" checked={todosMarcados} disabled={!lancaveis.length || ocupado === 'acao'}
                onChange={(e) => setSel(e.target.checked ? [...new Set([...sel, ...lancaveis])] : sel.filter((i) => !lancaveis.includes(i)))} title="Marca as linhas pendentes que já têm categoria e fornecedor" />
            </th>
            <th>Compra</th><th>Portador / cartão</th><th>Estabelecimento</th><th className="text-end">Valor</th><th style={{ minWidth: 260 }}>Categoria no Omie</th><th style={{ minWidth: 220 }}>Fornecedor no Omie</th><th className="text-end">Status</th></tr></thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan="8" className="text-muted">{filtro === 'pendentes' ? 'Nada pendente: tudo já está no Omie.' : 'Nenhuma linha.'}</td></tr>}
            {filtradas.map((l) => {
              const lista = prep.categorias[l.valor >= 0 ? 'R' : 'D'] || [];
              const podeEditar = pendente(l);
              return (
                <tr key={l.idx} className={feitos[l.idx] ? 'table-success' : sel.includes(l.idx) ? 'table-primary' : ''}>
                  <td>
                    {podeEditar && (
                      <input type="checkbox" className="form-check-input" aria-label={`Marcar linha ${l.idx}`} checked={sel.includes(l.idx)} disabled={ocupado === 'acao' || !lancavel(l)}
                        title={lancavel(l) ? '' : 'Escolha a categoria e o fornecedor antes de marcar'} onChange={(e) => setSel((s) => (e.target.checked ? [...s, l.idx] : s.filter((i) => i !== l.idx)))} />
                    )}
                  </td>
                  <td className="text-nowrap">{dataBr(l.data)}</td>
                  <td>{l.portador || <span className="text-muted">-</span>}</td>
                  <td>{l.estabelecimento}</td>
                  <td className={`text-end text-nowrap ${l.valor < 0 ? 'text-danger' : 'text-success'}`}>{brl(l.valor)}</td>
                  <td>
                    {podeEditar
                      ? <select className="form-select form-select-sm" value={categoriaDe(l)} onChange={(e) => setCatLinha((m) => ({ ...m, [l.idx]: e.target.value }))} disabled={ocupado === 'acao'}>
                          <option value="">Escolha…</option>
                          {lista.map((c) => <option key={c.codigo} value={c.codigo}>{c.descricao}</option>)}
                        </select>
                      : <span>{descCategoria(l)}</span>}
                    <div className="text-muted" style={{ fontSize: '.75rem' }}>
                      {l.sugestao
                        ? <>histórico: {l.sugestao.descricao} · {l.sugestao.vezes}× {l.sugestao.fonte === 'portador' ? 'neste cartão' : l.sugestao.fonte === 'credito' ? 'em créditos anteriores' : 'neste estabelecimento'}</>
                        : l.valor >= 0 ? <i>crédito: escolha uma categoria de receita</i> : <i>sem histórico no Omie</i>}
                    </div>
                  </td>
                  <td><Fornecedor l={l} /></td>
                  <td className="text-end text-nowrap"><Badge l={l} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>)}

      {modal && modal.tipo === 'lancar' && (() => {
        const l = modal.linha;
        return (
          <Modal titulo={l.valor >= 0 ? 'Lançar crédito na Pix Cards' : 'Lançar título no contas a pagar'} onFechar={() => setModal(null)} ocupado={ocupado === 'acao'}
            rodape={<>
              <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={ocupado === 'acao'}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado === 'acao'} onClick={() => lancarUma(l)}>
                <i className={`fas ${ocupado === 'acao' ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>Lançar no Omie
              </button>
            </>}>
            <div className="small">
              <div><b>Compra:</b> {dataBr(l.data)} · <b>Valor:</b> {brl(l.valor)}</div>
              <div><b>Estabelecimento:</b> {l.estabelecimento} · <b>Portador:</b> {l.portador || '-'}</div>
              <div><b>Categoria:</b> {descCategoria(l)}</div>
              <div><b>Pagamento:</b> {dataPg} (emissão, vencimento e baixa) · <b>Conta:</b> Pix Cards · {conciliar ? 'marcado como conciliado' : 'sem marcação de conciliado'}</div>
              {l.valor < 0 && <div><b>Fornecedor:</b> {fornDe(l)?.nome_omie} · cód. {fornCodigo(l)}</div>}
              {l.status === 'valor_igual' && <div className="alert alert-warning py-2 mt-2 mb-0">Já existe no Omie um lançamento de {brl(l.valor)} neste mês para "{l.fornecedor}". Confira se não é esta compra.</div>}
            </div>
          </Modal>
        );
      })()}

      {modal && modal.tipo === 'lote' && (() => {
        const itens = linhas.filter((l) => sel.includes(l.idx) && lancavel(l));
        const rodando = ocupado === 'acao';
        const terminou = lote && !rodando && lote.total > 0;
        return (
          <Modal titulo={`Lançar ${itens.length} linha(s) no Omie`} onFechar={() => { if (!rodando) { setModal(null); setLote(null); } }} ocupado={rodando}
            rodape={<>
              <button className="btn btn-secondary me-auto" onClick={() => { setModal(null); setLote(null); }} disabled={rodando}>{terminou ? 'Fechar' : 'Cancelar'}</button>
              {!terminou && <button className="btn btn-primary" disabled={rodando || !itens.length} onClick={lancarLote}>
                <i className={`fas ${rodando ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>Lançar um a um
              </button>}
            </>}>
            <div className="small">
              <div className="mb-2">Total: <b>{brl(itens.reduce((s, l) => s + l.valor, 0))}</b> · pagamento em <b>{dataPg}</b> · {conciliar ? 'conciliado' : 'não conciliado'}. Um título por chamada; para no primeiro erro.</div>
              {lote && (
                <div className="mb-2">
                  <div className="progress" style={{ height: 8 }}><div className="progress-bar" style={{ width: `${lote.total ? (lote.feitos / lote.total) * 100 : 0}%` }}></div></div>
                  <div className="text-muted mt-1">{lote.feitos} de {lote.total}{lote.atual ? ` · lançando ${lote.atual.estabelecimento} ${brl(lote.atual.valor)}…` : ''}</div>
                  {lote.erros.map((e) => <div key={e.idx} className="text-danger">Parou em {e.estabelecimento} {brl(e.valor)}: {e.erro}</div>)}
                </div>
              )}
              <table className="table table-sm mb-0" style={{ fontSize: '.8rem' }}>
                <tbody>
                  {itens.map((l) => <tr key={l.idx} className={feitos[l.idx] ? 'table-success' : ''}>
                    <td>{dataBr(l.data)}</td><td>{l.estabelecimento}</td><td className="text-end">{brl(l.valor)}</td><td>{descCategoria(l)}</td>
                    <td className="text-end">{feitos[l.idx] ? `cód. ${feitos[l.idx].codigo}` : ''}</td>
                  </tr>)}
                </tbody>
              </table>
            </div>
          </Modal>
        );
      })()}
    </div></div>
  );
}
